import type { ClipboardEvent } from 'react';

/** Four real digits: 0100 is a half-typed year, not a date a provider means. */
const MIN_YEAR = 1000;

const ISO_ORDER = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/;
const US_ORDER = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/;
const COMPACT_US_ORDER = /^(\d{2})(\d{2})(\d{4})$/;

/**
 * The calendar date in typed or pasted text, as the ISO `YYYY-MM-DD` the forms hold, or null.
 *
 * Accepts `YYYY-MM-DD` (also `/` or `.`), and the US order the portal's providers write —
 * `MM/DD/YYYY`, `M/D/YYYY`, `MM-DD-YYYY`, `MM.DD.YYYY` or `MMDDYYYY`. The year must have four digits (1000 or later) and
 * the date must exist: a two-digit year or `02/30/2026` is refused, never guessed at, because a date of
 * birth that is silently wrong finds the wrong policy.
 */
export function parsePastedDate(text: string): string | null {
  const value = text.trim();
  const iso = ISO_ORDER.exec(value);
  const us = US_ORDER.exec(value) ?? COMPACT_US_ORDER.exec(value);

  const [year, month, day] = iso
    ? [iso[1], iso[2], iso[3]]
    : us
      ? [us[3], us[1], us[2]]
      : [undefined, undefined, undefined];
  if (year === undefined || month === undefined || day === undefined) {
    return null;
  }

  const [y, m, d] = [Number(year), Number(month), Number(day)];
  const date = new Date(Date.UTC(y, m - 1, d));
  const real =
    y >= MIN_YEAR &&
    date.getUTCFullYear() === y &&
    date.getUTCMonth() === m - 1 &&
    date.getUTCDate() === d;

  return real ? `${year}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}` : null;
}

/**
 * Lays typed digits out as `MM/DD/YYYY` while the user types: `0729` becomes `07/29`, `07291954` becomes
 * `07/29/1954`. A slash is added only once the digit after it exists, so deleting back through one never
 * fights the cursor. Text that is not just digits and slashes (an ISO `1954-07-29`, a pasted sentence) is
 * left exactly as typed, and the date it spells is still understood by {@link parsePastedDate}.
 */
export function maskUsDate(typed: string): string {
  if (!/^[\d/]*$/.test(typed)) {
    return typed;
  }

  const digits = typed.replace(/\D/g, '').slice(0, 8);
  const month = digits.slice(0, 2);
  const day = digits.slice(2, 4);
  const year = digits.slice(4);

  return [month, day, year].filter((part) => part.length > 0).join('/');
}

/**
 * An ISO `YYYY-MM-DD` value as the `MM/DD/YYYY` the portal always shows, whatever the browser's locale
 * (a Latin American browser would show day first). Empty or malformed input gives an empty string.
 */
export function formatUsDate(isoDate: string): string {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);

  return parts === null ? '' : `${parts[2] ?? ''}/${parts[3] ?? ''}/${parts[1] ?? ''}`;
}

/**
 * A paste handler for a date input. A browser's own paste into `<input type="date">` only works when
 * the text happens to match the locale's exact format, so this reads the clipboard text itself and, when
 * it is a date, sets it through the field's normal `onChange` path — validation and bounds are the
 * field's own. Text that is not a date is left to the browser, which ignores it.
 */
export function pasteDate(
  apply: (isoDate: string) => void,
): (event: ClipboardEvent<HTMLInputElement>) => void {
  return (event): void => {
    const parsed = parsePastedDate(event.clipboardData.getData('text'));
    if (parsed === null) {
      return;
    }

    event.preventDefault();
    apply(parsed);
  };
}
